import React, { useState, useEffect } from 'react';
import request from '../../../services/Request';
import ModernTable, { ModernPageContainer, ModernPageHeader } from '../components/ModernTable';
import CustomModal from '../components/CustomModal';
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Chip,
  ButtonGroup,
  Box,
  alpha,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LoadingIcon from '../../items/buttons/LoadingIcon';
import BackButton from '../../items/buttons/BackButton';
import Swal from 'sweetalert2';
import TimezoneSelect from 'react-timezone-select';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';

export default function ManageHFUsers() {
  const theme = useTheme();
  const user = JSON.parse(localStorage.getItem('user'));
  const referTo = user?.role === 'Admin' ? user?.email : user?.referTo;

  const [hfUsers, setHfUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [viewMode, setViewMode] = useState('users'); // 'users' or 'divisions'

  // User Dialog State
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({
    _id: null,
    userID: '',
    cardNumber: '',
    surname: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    division: '',
    badgeEnabled: false,
  });
  const [isEditMode, setIsEditMode] = useState(false);

  // Division Dialog State
  const [divisionDialogOpen, setDivisionDialogOpen] = useState(false);
  const [divisionFormData, setDivisionFormData] = useState({
    _id: null,
    name: '',
    isDefault: false,
    timezone: 'UTC',
    limits: [],
  });
  const [isDivisionEditMode, setIsDivisionEditMode] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [refresh]);

  const loadData = async () => {
    try {
      setIsLoading(false);

      // Load HF Users
      const usersRes = await request({
        method: 'get',
        url: `/hfusers/by-account/${referTo}`,
      });
      setHfUsers(usersRes.data);

      // Load Divisions
      const divisionsRes = await request({
        method: 'get',
        url: `/divisions/by-account/${referTo}`,
      });
      setDivisions(divisionsRes.data);

      // Load Categories
      const categoriesRes = await request({
        method: 'get',
        url: `/categories/${referTo}`,
      });
      setCategories(categoriesRes.data);

      setIsLoading(true);
    } catch (error) {
      Swal.fire({
        text: `Error loading data: ${error.message}`,
        icon: 'error',
      });
      setIsLoading(true);
    }
  };

  // User CRUD Functions
  const openUserDialog = (userData = null) => {
    if (userData) {
      setUserFormData({
        _id: userData._id,
        userID: userData.userID || '',
        cardNumber: userData.cardNumber,
        surname: userData.surname,
        name: userData.name,
        email: userData.email || '',
        phone: userData.phone || '',
        password: '',
        division: userData.division?._id || '',
        badgeEnabled: userData.badgeEnabled,
      });
      setIsEditMode(true);
    } else {
      setUserFormData({
        _id: null,
        userID: '',
        cardNumber: '',
        surname: '',
        name: '',
        email: '',
        phone: '',
        password: '',
        division: divisions.find((d) => d.isDefault)?._id || '',
        badgeEnabled: false,
      });
      setIsEditMode(false);
    }
    setUserDialogOpen(true);
  };

  const handleUserFormChange = (field, value) => {
    setUserFormData({ ...userFormData, [field]: value });
  };

  const saveUser = async () => {
    try {
      if (
        !userFormData.cardNumber ||
        !userFormData.surname ||
        !userFormData.name ||
        !userFormData.division
      ) {
        Swal.fire({
          text: 'Please fill in all required fields',
          icon: 'warning',
        });
        return;
      }

      const url = isEditMode ? `/hfusers/${userFormData._id}` : '/hfusers/create';
      const method = isEditMode ? 'put' : 'post';

      await request({
        method,
        url,
        data: {
          ...userFormData,
          referTo,
        },
      });

      Swal.fire({
        text: `User ${isEditMode ? 'updated' : 'created'} successfully`,
        icon: 'success',
      });

      setUserDialogOpen(false);
      setRefresh(!refresh);
    } catch (error) {
      Swal.fire({
        text: `Error: ${error.response?.data || error.message}`,
        icon: 'error',
      });
    }
  };

  const toggleUserDisabled = async (userData) => {
    const action = userData.disabled ? 'enable' : 'disable';
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `${action.charAt(0).toUpperCase() + action.slice(1)} user: ${userData.name} ${userData.surname}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: userData.disabled ? '#3085d6' : '#d33',
      cancelButtonColor: '#aaa',
      confirmButtonText: `Yes, ${action}!`,
    });

    if (result.isConfirmed) {
      try {
        await request({
          method: 'patch',
          url: `/hfusers/${userData._id}/toggle-disabled`,
        });

        Swal.fire({
          text: `User ${action}d successfully`,
          icon: 'success',
        });

        setRefresh(!refresh);
      } catch (error) {
        Swal.fire({
          text: `Error: ${error.response?.data || error.message}`,
          icon: 'error',
        });
      }
    }
  };

  const deleteUser = async (userData) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete user: ${userData.name} ${userData.surname}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await request({
          method: 'delete',
          url: `/hfusers/${userData._id}`,
        });

        Swal.fire({
          text: 'User deleted successfully',
          icon: 'success',
        });

        setRefresh(!refresh);
      } catch (error) {
        Swal.fire({
          text: `Error: ${error.response?.data || error.message}`,
          icon: 'error',
        });
      }
    }
  };

  // Division CRUD Functions
  const openDivisionDialog = (divisionData = null) => {
    if (divisionData) {
      // Convert limits to use category IDs instead of objects
      const formattedLimits = (divisionData.limits || []).map((limit) => ({
        category: limit.category?._id || limit.category || '',
        quantity: limit.quantity,
        period: limit.period,
      }));

      setDivisionFormData({
        _id: divisionData._id,
        name: divisionData.name,
        isDefault: divisionData.isDefault,
        timezone: divisionData.timezone || 'UTC',
        limits: formattedLimits,
      });
      setIsDivisionEditMode(true);
    } else {
      setDivisionFormData({
        _id: null,
        name: '',
        isDefault: false,
        timezone: 'UTC',
        limits: [],
      });
      setIsDivisionEditMode(false);
    }
    setDivisionDialogOpen(true);
  };

  const handleDivisionFormChange = (field, value) => {
    setDivisionFormData({ ...divisionFormData, [field]: value });
  };

  const addLimit = () => {
    setDivisionFormData({
      ...divisionFormData,
      limits: [...divisionFormData.limits, { category: '', quantity: 1, period: 'week' }],
    });
  };

  const updateLimit = (index, field, value) => {
    const newLimits = [...divisionFormData.limits];
    newLimits[index][field] = value;
    setDivisionFormData({ ...divisionFormData, limits: newLimits });
  };

  const removeLimit = (index) => {
    const newLimits = divisionFormData.limits.filter((_, i) => i !== index);
    setDivisionFormData({ ...divisionFormData, limits: newLimits });
  };

  const saveDivision = async () => {
    try {
      // Validate limits: category must be selected if limit exists
      const invalidLimits = divisionFormData.limits.filter(
        (limit) => !limit.category || !limit.category.trim()
      );

      if (invalidLimits.length > 0) {
        Swal.fire({
          text: 'Please select a category for all limits or remove empty limits',
          icon: 'warning',
        });
        return;
      }

      // If name is empty, set to "Default" and mark as default
      const divisionName = divisionFormData.name.trim() || 'Default';
      const isDefaultDivision = divisionName === 'Default' || !divisionFormData.name.trim();

      const url = isDivisionEditMode ? `/divisions/${divisionFormData._id}` : '/divisions/create';
      const method = isDivisionEditMode ? 'put' : 'post';

      await request({
        method,
        url,
        data: {
          name: divisionName,
          isDefault: isDefaultDivision,
          timezone: divisionFormData.timezone || 'UTC',
          limits: divisionFormData.limits,
          referTo,
        },
      });

      Swal.fire({
        text: `Division ${isDivisionEditMode ? 'updated' : 'created'} successfully`,
        icon: 'success',
      });

      setDivisionDialogOpen(false);
      setRefresh(!refresh);
    } catch (error) {
      Swal.fire({
        text: `Error: ${error.response?.data || error.message}`,
        icon: 'error',
      });
    }
  };

  const deleteDivision = async (divisionData) => {
    if (divisionData.isDefault) {
      Swal.fire({
        text: 'Cannot delete the default division',
        icon: 'error',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete division: ${divisionData.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await request({
          method: 'delete',
          url: `/divisions/${divisionData._id}`,
        });

        Swal.fire({
          text: 'Division deleted successfully',
          icon: 'success',
        });

        setRefresh(!refresh);
      } catch (error) {
        Swal.fire({
          text: `Error: ${error.response?.data || error.message}`,
          icon: 'error',
        });
      }
    }
  };

  // Table columns
  const userColumns = [
    {
      title: 'Badge ID',
      field: 'userID',
      render: (rowData) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary }}
        >
          {rowData.userID || '-'}
        </Typography>
      ),
    },
    {
      title: 'Card #',
      field: 'cardNumber',
      render: (rowData) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.secondary }}
        >
          {rowData.cardNumber || '-'}
        </Typography>
      ),
    },
    {
      title: 'Name',
      field: 'surname',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {rowData.surname} {rowData.name}
        </Typography>
      ),
    },
    {
      title: 'Email',
      field: 'email',
      render: (rowData) => (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
          }}
        >
          {rowData.email}
        </Typography>
      ),
    },
    {
      title: 'Division',
      field: 'division.name',
      render: (rowData) => (
        <Chip
          label={rowData.division?.name || 'Default'}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: rowData.division?.isDefault
              ? alpha(theme.palette.grey[500], 0.12)
              : alpha(theme.palette.primary.main, 0.12),
            color: rowData.division?.isDefault
              ? theme.palette.text.secondary
              : theme.palette.primary.dark,
            border: `1.5px solid ${
              rowData.division?.isDefault
                ? alpha(theme.palette.grey[500], 0.3)
                : alpha(theme.palette.primary.main, 0.3)
            }`,
          }}
        />
      ),
    },
    {
      title: 'Badge',
      field: 'badgeEnabled',
      render: (rowData) => (
        <Chip
          label={rowData.badgeEnabled ? 'Yes' : 'No'}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: rowData.badgeEnabled
              ? alpha(theme.palette.success.main, 0.12)
              : alpha(theme.palette.error.main, 0.12),
            color: rowData.badgeEnabled ? theme.palette.success.dark : theme.palette.error.dark,
            border: `1.5px solid ${
              rowData.badgeEnabled
                ? alpha(theme.palette.success.main, 0.3)
                : alpha(theme.palette.error.main, 0.3)
            }`,
          }}
        />
      ),
    },
    {
      title: 'Status',
      field: 'disabled',
      render: (rowData) => (
        <Chip
          label={rowData.disabled ? 'Disabled' : 'Active'}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: rowData.disabled
              ? alpha(theme.palette.error.main, 0.12)
              : alpha(theme.palette.success.main, 0.12),
            color: rowData.disabled ? theme.palette.error.dark : theme.palette.success.dark,
            border: `1.5px solid ${
              rowData.disabled
                ? alpha(theme.palette.error.main, 0.3)
                : alpha(theme.palette.success.main, 0.3)
            }`,
          }}
        />
      ),
    },
  ];

  const divisionColumns = [
    {
      title: 'Division Name',
      field: 'name',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          {rowData.name || 'Default'}
        </Typography>
      ),
    },
    {
      title: 'Default',
      field: 'isDefault',
      render: (rowData) => (
        <Chip
          label={rowData.isDefault ? 'Yes' : 'No'}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: rowData.isDefault
              ? alpha(theme.palette.success.main, 0.12)
              : alpha(theme.palette.grey[500], 0.12),
            color: rowData.isDefault ? theme.palette.success.dark : theme.palette.text.secondary,
            border: `1.5px solid ${
              rowData.isDefault
                ? alpha(theme.palette.success.main, 0.3)
                : alpha(theme.palette.grey[500], 0.3)
            }`,
          }}
        />
      ),
    },
    {
      title: 'Limits',
      field: 'limits',
      render: (rowData) => (
        <Chip
          label={`${rowData.limits?.length || 0} limit(s)`}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.info.main, 0.12),
            color: theme.palette.info.dark,
            border: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        />
      ),
    },
  ];

  return (
    <ModernPageContainer>
      {!isLoading && <LoadingIcon />}

      <ModernPageHeader
        title="Users and divisions Management"
        subtitle="Manage users, divisions, and purchase limits"
        icon={PeopleIcon}
        buttonText={viewMode === 'divisions' ? 'Add Division' : undefined}
        buttonIcon={viewMode === 'divisions' ? AddIcon : undefined}
        onButtonClick={viewMode === 'divisions' ? () => openDivisionDialog() : undefined}
      />

      <Paper
        sx={{
          p: 0.8,
          mb: 1.6,
          mt: 1.6,
          borderRadius: 2.4,
          display: 'inline-flex',
          boxShadow: `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.08)}`,
          border: `0.8px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <ButtonGroup
          sx={{
            gap: 0.8,
            '& .MuiButtonGroup-grouped': {
              border: 0,
            },
          }}
        >
          <Button
            onClick={() => setViewMode('users')}
            startIcon={<PeopleIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              px: 2.4,
              py: 1.2,
              borderRadius: 1.6,
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'none',
              color: viewMode === 'users' ? 'white' : theme.palette.text.secondary,
              background:
                viewMode === 'users'
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : 'transparent',
              boxShadow:
                viewMode === 'users'
                  ? `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.3)}`
                  : 'none',
              '&:hover': {
                background:
                  viewMode === 'users'
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    : alpha(theme.palette.primary.main, 0.08),
                transform: 'translateY(-1.6px)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Users
          </Button>
          <Button
            onClick={() => setViewMode('divisions')}
            startIcon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              px: 2.4,
              py: 1.2,
              borderRadius: 1.6,
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'none',
              color: viewMode === 'divisions' ? 'white' : theme.palette.text.secondary,
              background:
                viewMode === 'divisions'
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : 'transparent',
              boxShadow:
                viewMode === 'divisions'
                  ? `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.3)}`
                  : 'none',
              '&:hover': {
                background:
                  viewMode === 'divisions'
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    : alpha(theme.palette.primary.main, 0.08),
                transform: 'translateY(-1.6px)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Divisions
          </Button>
        </ButtonGroup>
      </Paper>

      <Box sx={{ mt: 3 }}>
        {/* Users Table */}
        {viewMode === 'users' && (
          <ModernTable
            columns={userColumns}
            data={hfUsers}
            actions={[
              {
                icon: 'edit',
                tooltip: 'Edit User',
                onClick: (event, rowData) => openUserDialog(rowData),
              },
              (rowData) => ({
                icon: rowData.disabled ? 'lock_open' : 'lock',
                tooltip: rowData.disabled ? 'Enable User' : 'Disable User',
                onClick: (event, row) => toggleUserDisabled(row),
              }),
              {
                icon: 'delete',
                tooltip: 'Delete User',
                onClick: (event, rowData) => deleteUser(rowData),
              },
            ]}
            options={{
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        )}

        {/* Divisions Table */}
        {viewMode === 'divisions' && (
          <ModernTable
            columns={divisionColumns}
            data={divisions}
            actions={[
              {
                icon: 'edit',
                tooltip: 'Edit Division',
                onClick: (event, rowData) => openDivisionDialog(rowData),
              },
              {
                icon: 'delete',
                tooltip: 'Delete Division',
                onClick: (event, rowData) => deleteDivision(rowData),
              },
            ]}
            options={{
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
            }}
          />
        )}

        <Box sx={{ mt: 2 }}>
          <BackButton />
        </Box>
      </Box>

      {/* User Dialog */}
      <CustomModal show={userDialogOpen} onHide={() => setUserDialogOpen(false)} size="sm" centered>
        <CustomModal.Header closeButton>
          <CustomModal.Title>{isEditMode ? 'Edit User' : 'Add New User'}</CustomModal.Title>
        </CustomModal.Header>
        <CustomModal.Body>
          <TextField
            label="Badge ID"
            value={userFormData.userID}
            onChange={(e) => handleUserFormChange('userID', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Card #"
            value={userFormData.cardNumber}
            onChange={(e) => handleUserFormChange('cardNumber', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Surname *"
            value={userFormData.surname}
            onChange={(e) => handleUserFormChange('surname', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Name *"
            value={userFormData.name}
            onChange={(e) => handleUserFormChange('name', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={userFormData.email}
            onChange={(e) => handleUserFormChange('email', e.target.value)}
            fullWidth
            margin="normal"
            disabled={isEditMode}
          />
          <TextField
            label="Phone"
            value={userFormData.phone}
            onChange={(e) => handleUserFormChange('phone', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Password"
            type="password"
            value={userFormData.password}
            onChange={(e) => handleUserFormChange('password', e.target.value)}
            fullWidth
            margin="normal"
            placeholder={isEditMode ? 'Leave blank to keep current password' : ''}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="division-select-label">Division *</InputLabel>
            <Select
              labelId="division-select-label"
              value={userFormData.division}
              onChange={(e) => handleUserFormChange('division', e.target.value)}
              label="Division *"
            >
              {divisions.map((div) => (
                <MenuItem key={div._id} value={div._id}>
                  {div.name} {div.isDefault ? '(Default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={userFormData.badgeEnabled}
                onChange={(e) => handleUserFormChange('badgeEnabled', e.target.checked)}
                color="primary"
              />
            }
            label="Badge Enabled"
          />
        </CustomModal.Body>
        <CustomModal.Footer>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveUser} color="primary" variant="contained">
            Save
          </Button>
        </CustomModal.Footer>
      </CustomModal>

      {/* Division Dialog */}
      <CustomModal
        show={divisionDialogOpen}
        onHide={() => setDivisionDialogOpen(false)}
        size="md"
        centered
      >
        <CustomModal.Header closeButton>
          <CustomModal.Title>
            {isDivisionEditMode ? 'Edit Division' : 'Add New Division'}
          </CustomModal.Title>
        </CustomModal.Header>
        <CustomModal.Body>
          <TextField
            label="Division Name (leave empty for Default) *"
            value={divisionFormData.name}
            onChange={(e) => handleDivisionFormChange('name', e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., Marketing, Sales, or leave empty for Default"
          />

          <Box marginTop={2} marginBottom={2} sx={{ position: 'relative', zIndex: 10 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Timezone
            </Typography>
            <Box sx={{ '& > div': { width: '100%' } }}>
              <TimezoneSelect
                value={divisionFormData.timezone}
                onChange={(timezone) => handleDivisionFormChange('timezone', timezone.value)}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    width: '100%',
                    minWidth: '100%',
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.body}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.5)
                  : alpha(theme.palette.grey[100], 1),
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Purchase Limits
            </Typography>
            {divisionFormData.limits.map((limit, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1,
                  p: 1,
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel id={`category-label-${index}`}>Category</InputLabel>
                      <Select
                        labelId={`category-label-${index}`}
                        value={limit.category}
                        onChange={(e) => updateLimit(index, 'category', e.target.value)}
                        label="Category"
                      >
                        {categories.map((cat) => (
                          <MenuItem key={cat._id} value={cat._id}>
                            {cat.name?.en || 'Unknown'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={limit.quantity}
                      onChange={(e) => updateLimit(index, 'quantity', parseInt(e.target.value))}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel id={`period-label-${index}`}>Period</InputLabel>
                      <Select
                        labelId={`period-label-${index}`}
                        value={limit.period}
                        onChange={(e) => updateLimit(index, 'period', e.target.value)}
                        label="Period"
                      >
                        <MenuItem value="day">Per Day</MenuItem>
                        <MenuItem value="week">Per Week</MenuItem>
                        <MenuItem value="month">Per Month</MenuItem>
                        <MenuItem value="year">Per Year</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <Button onClick={() => removeLimit(index)} color="secondary">
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button onClick={addLimit} color="primary" sx={{ mt: 2 }}>
              + Add Limit
            </Button>
          </Box>
        </CustomModal.Body>
        <CustomModal.Footer>
          <Button onClick={() => setDivisionDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveDivision} color="primary" variant="contained">
            Save
          </Button>
        </CustomModal.Footer>
      </CustomModal>
    </ModernPageContainer>
  );
}
